import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { lookupItemSchema } from '@/types/lookupItem.types';
import {
  optionalDateSchema,
  optionalPositiveIdSchema,
  optionalTextSchema,
} from '@/utils/searchParams';
import {
  TEACHER_CATEGORY,
  TEACHER_GENDER,
  TEACHER_MARITAL_STATUS,
  TEACHER_PRIORITY,
  TEACHER_STATUS,
  TEACHING_MEDIUM,
} from '../constants/teacherSubmission.constants';

export type TeacherCategory = (typeof TEACHER_CATEGORY)[number];
export type TeacherStatus = (typeof TEACHER_STATUS)[number];

export const teacherSubmissionSearchSchema = z.object({
  search: optionalTextSchema,
  instituteId: optionalPositiveIdSchema,
  teacherStatus: z.enum(TEACHER_STATUS).optional().catch(undefined),
  districtId: optionalPositiveIdSchema,
  thanaId: optionalPositiveIdSchema,
  designationId: optionalPositiveIdSchema,
  category: z.enum(TEACHER_CATEGORY).optional().catch(undefined),
  amUserId: optionalPositiveIdSchema,
  updatedBy: optionalPositiveIdSchema,
  updatedFrom: optionalDateSchema,
  updatedTo: optionalDateSchema,
});

function enumOrNull<const Values extends readonly [string, ...string[]]>(values: Values) {
  return z.enum(values).nullable().catch(null);
}

const teacherListItemSchema = z.object({
  teacherId: z.coerce.number(),
  name: z.string(),
  mobile: z.string(),
  designationName: z.string().nullable(),
  category: enumOrNull(TEACHER_CATEGORY),
  instituteId: z.coerce.number(),
  instituteName: z.string(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  appUpdatedAt: z.string().nullable(),
  appUpdatedBy: z.coerce.number().nullable(),
  appUpdatedByName: z.string().nullable(),
  status: z.enum(TEACHER_STATUS),
});

export const paginatedTeachersSchema = z.object({
  data: z.array(teacherListItemSchema),
  meta: paginationMetaSchema,
});

export const teacherSummarySchema = z.object({
  total: z.number(),
  active: z.number(),
  inactive: z.number(),
  recentlyUpdated: z.number(),
});

const subjectExpertiseGroupSchema = z.object({
  classId: z.coerce.number(),
  className: z.string(),
  subjects: z.array(lookupItemSchema),
});

export const teacherDetailSchema = z.object({
  teacherId: z.coerce.number(),
  institute: lookupItemSchema,
  name: z.string(),
  mobile: z.string(),
  secondaryMobile: z.string().nullable(),
  gender: enumOrNull(TEACHER_GENDER),
  dateOfBirth: z.string().nullable(),
  photoUrl: z.string().nullable(),
  designation: lookupItemSchema.nullable(),
  classes: z.array(lookupItemSchema),
  mainSubjects: z.array(lookupItemSchema),
  subjectExpertise: z.array(subjectExpertiseGroupSchema),
  category: enumOrNull(TEACHER_CATEGORY),
  teacherPriority: enumOrNull(TEACHER_PRIORITY),
  registrationId: z.string().nullable(),
  nidNumber: z.string().nullable(),
  highestDegree: z.string().nullable(),
  passingDate: z.string().nullable(),
  passingInstitute: z.string().nullable(),
  teachingMedium: enumOrNull(TEACHING_MEDIUM),
  maritalStatus: enumOrNull(TEACHER_MARITAL_STATUS),
  retirementDate: z.string().nullable(),
  teachingStartDate: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  appUpdatedAt: z.string().nullable(),
  appUpdatedByName: z.string().nullable(),
  appUpdatedByHrEmpId: z.string().nullable(),
  appUpdatedByAmName: z.string().nullable(),
  status: z.enum(TEACHER_STATUS),
});

export const teacherDesignationListSchema = z.array(lookupItemSchema);

export type TeacherSubmissionSearch = z.infer<typeof teacherSubmissionSearchSchema>;
export type TeacherListItem = z.infer<typeof teacherListItemSchema>;
export type PaginatedTeachers = z.infer<typeof paginatedTeachersSchema>;
export type TeacherSummary = z.infer<typeof teacherSummarySchema>;
export type TeacherDetail = z.infer<typeof teacherDetailSchema>;
export type TeacherLookupItem = z.infer<typeof lookupItemSchema>;

export interface TeacherSummaryParams {
  instituteId?: number | undefined;
  districtId?: number | undefined;
  thanaId?: number | undefined;
  designationId?: number | undefined;
  category?: TeacherCategory | undefined;
  amUserId?: number | undefined;
  updatedBy?: number | undefined;
  updatedFrom?: string | undefined;
  updatedTo?: string | undefined;
  search?: string | undefined;
}

export interface TeacherListParams extends TeacherSummaryParams {
  status?: TeacherStatus | undefined;
  pageSize: number;
}

export type TeacherCountsBySubmitterParams = Omit<
  TeacherListParams,
  'amUserId' | 'updatedBy' | 'pageSize'
>;
